import {
  ParentRow,
  ParentRowWrapper,
  ParentText,
  ParentTextWrapper,
  ParentClickContainer,
  CheckboxAligner,
  FirstChildrenWrapper,
  ArrowIconContainer,
} from './Styles'
import { useState, useEffect } from 'react'
import { Form, Popup, Button, Text } from '@/ui'
import ChildDepartmentRow from './ChildDepartmentRow'
import { Department } from './index'
import {
  HasChildrenMatchingSearch,
  HasSelectedChildren,
  AllChildrenSelected,
  MergeChildrenWithSelected,
  GetAllChildren,
} from './helpers'
import { ReactComponent as ArrowDown } from '@/assets/images/icons/arrows-solid-down-custom.svg'

import { useTranslation } from 'react-i18next'

interface DepartmentsRowProps {
  department: Department
  searchTerm: string
  currentIndex: number | undefined
  onChange: (departments: Department[]) => void
  handleSelectSingleDepartment: (department: Department) => void
  selectedDepartments: Department[]
  indent?: number
  allowArrows?: boolean
  multiple?: boolean
  disabledDepartments?: string[]
}

function countChildDepartments(node: Department) {
  if (!node.children || node.children.length === 0) {
    return 0
  }
  let count = node.children.length
  node.children.forEach(child => {
    count += countChildDepartments(child)
  })
  return count
}
export default function ParentDepartmentsRow({
  department,
  searchTerm,
  onChange,
  selectedDepartments,
  multiple,
  disabledDepartments,
  handleSelectSingleDepartment,
  currentIndex,
  allowArrows,
}: DepartmentsRowProps) {
  const { t } = useTranslation()
  const isDisabled = disabledDepartments
    ? disabledDepartments.includes(department.id)
    : false
  const isSelected = selectedDepartments?.some(dep => dep.id === department.id)
  const hasChildrenMatchingSearch = HasChildrenMatchingSearch(
    department,
    searchTerm,
  )
  const hasSelectedChildren = HasSelectedChildren(
    department,
    selectedDepartments,
  )
  const [open, setOpen] = useState(
    hasSelectedChildren || hasChildrenMatchingSearch,
  )
  const children = department.children || []
  const hasChildren = department.children && children.length > 0
  const allChildrenSelected = hasChildren
    ? AllChildrenSelected(children, selectedDepartments)
    : false
  const isHovering = currentIndex === department.index
  const hasHovered = currentIndex && department.index < currentIndex

  useEffect(() => {
    if (searchTerm || isHovering || hasHovered || hasSelectedChildren) {
      if (
        hasChildrenMatchingSearch ||
        isHovering ||
        hasHovered ||
        hasSelectedChildren
      ) {
        setOpen(true)
      } else setOpen(false)
    } else setOpen(false)
  }, [hasChildrenMatchingSearch, currentIndex])

  function HandleClick(e: any) {
    e.stopPropagation()
    if (isDisabled) {
      if (hasChildren) {
        setOpen(!open)
      }
      return
    }
    handleSelectSingleDepartment(department)
  }
  const numberOfChildDepartments = department?.children
    ? countChildDepartments(department)
    : 0

  function HandlePopupClick(e: any, option: 'this' | 'children' | 'both') {
    const allChildren = GetAllChildren(children)

    switch (option) {
      case 'this':
        handleSelectSingleDepartment(department)
        break
      case 'children':
        if (hasSelectedChildren && !allChildrenSelected) {
          onChange(MergeChildrenWithSelected(allChildren, selectedDepartments))
        }
        if (!hasSelectedChildren && !allChildrenSelected) {
          onChange([...selectedDepartments, ...allChildren])
        }
        if (allChildrenSelected) {
          onChange(
            selectedDepartments.filter(
              dep => !allChildren?.some(child => child.id === dep.id),
            ),
          )
        }
        break
    }

    e.stopPropagation()
    e.preventDefault()
  }

  return (
    <ParentRowWrapper
      onKeyUp={(e: any) => {
        if (e.key === 'Enter') {
          if (!multiple) {
            HandleClick(e)
          } else setOpen(!open)
          e.stopPropagation()
        }
      }}>
      <ParentRow
        data-department-row={`${department.index}`}
        isHovering={isHovering}
        selected={isSelected}>
        <ParentTextWrapper>
          {hasChildren ? (
            <ArrowIconContainer
              open={open}
              onClick={e => {
                setOpen(!open)
                e.stopPropagation()
              }}>
              <ArrowDown></ArrowDown>
            </ArrowIconContainer>
          ) : allowArrows ? (
            <div style={{ width: '20px' }}></div>
          ) : undefined}
          <ParentClickContainer
            onClick={(e: any) => {
              HandleClick(e)
              e.stopPropagation()
            }}>
            {multiple && (
              <div onClick={e => e.stopPropagation()}>
                <Popup
                  on="click"
                  trigger={
                    <CheckboxAligner>
                      <Form.Checkbox
                        checked={isSelected}
                        onChange={e => {
                          if (!hasChildren) {
                            HandleClick(e)
                          } else {
                            return null
                          }
                        }}
                      />
                    </CheckboxAligner>
                  }
                  content={
                    <div
                      onClick={e => e.stopPropagation()}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}>
                      <Button.Cancel
                        onClick={e => HandlePopupClick(e, 'this')}
                        fluid
                        content={
                          isSelected ? t('Remove this') : t('Add this')
                        }></Button.Cancel>
                      <Button.Cancel
                        onClick={e => HandlePopupClick(e, 'children')}
                        style={{ padding: '8px' }}
                        fluid
                        content={
                          allChildrenSelected
                            ? t('Remove all sub-departments')
                            : t('Add all sub-departments')
                        }></Button.Cancel>
                    </div>
                  }
                  position="top center"
                  hoverable
                  flowing
                />
              </div>
            )}
            <Popup
              on="hover"
              disabled={!isDisabled}
              trigger={
                <ParentText
                  isDisabled={isDisabled}
                  onClick={(e: any) => {
                    HandleClick(e)

                    e.stopPropagation()
                  }}>
                  {department.name}
                </ParentText>
              }
              content={
                <Text.Span>
                  {t('This department is already selected')}
                </Text.Span>
              }
              position="top center"
              flowing
            />
            {numberOfChildDepartments > 0 && (
              <span
                style={{ opacity: '0.5' }}
                onClick={(e: any) => {
                  HandleClick(e)
                  e.stopPropagation()
                }}>
                ({numberOfChildDepartments})
              </span>
            )}
          </ParentClickContainer>
        </ParentTextWrapper>
      </ParentRow>
      {open && department.children && department.children.length > 0 && (
        <FirstChildrenWrapper>
          {department.children?.map(department => (
            <ChildDepartmentRow
              department={department}
              searchTerm={searchTerm}
              currentIndex={currentIndex}
              selectedDepartments={selectedDepartments}
              onChange={onChange}
              handleSelectSingleDepartment={handleSelectSingleDepartment}
              indent={35}
              key={department.id}
              multiple={multiple}
              disabledDepartments={disabledDepartments}></ChildDepartmentRow>
          ))}
        </FirstChildrenWrapper>
      )}
    </ParentRowWrapper>
  )
}
